import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { Active } from '../../../../../types';
import { useGetContactNotes } from '../../../../../hooks/queries/contactNotes';
import { useBusinessRelationTypeGate } from '../../../../../hooks/queries/businessRelationType';
import { toContactNoteUids } from '../../../mappers/contactNoteMapper';
import type { SaveBlocker } from '../../utils/saveGate';
import { useLinkedContactNotes } from '../../hooks/useLinkedContactNotes';

type Props = {
  serviceAgreement: ServiceAgreementV2;
  brUid?: string;
  addsServiceRoles?: boolean;
  linksContactNotes: boolean;
};

/**
 * Contact notes linked while configuring a product. Only the notes linked in this session are
 * listed, the ones the product already has stay out of the grid.
 */
export const useProductContactNotes = ({
  serviceAgreement,
  brUid,
  addsServiceRoles = false,
  linksContactNotes,
}: Props) => {
  const { t } = useTranslation();
  const { isActiveBr } = useBusinessRelationTypeGate(linksContactNotes ? brUid : undefined);
  const { data: existingNotes } = useGetContactNotes(
    linksContactNotes ? serviceAgreement.serviceAgreementId : undefined,
  );
  const { notes, changeNotes } = useLinkedContactNotes();
  const existingNoteIds = useMemo(() => (existingNotes ?? []).map((note) => note.contactNoteId), [existingNotes]);

  const newNotes = useMemo(
    () => notes.filter((note) => !existingNoteIds.includes(note.contactNoteId)),
    [notes, existingNoteIds],
  );

  const isActiveProduct = serviceAgreement.agreementStatus === Active.ACTIVE;
  const isContactNoteRequired = linksContactNotes && isActiveBr && isActiveProduct && addsServiceRoles;
  const blocker: SaveBlocker = {
    blocked: isContactNoteRequired && newNotes.length === 0,
    reason: t('pb.contactnotes.required.cardholders', 'A contact note is required to add one or more card holders.'),
  };

  const uidsForSave = useMemo(() => {
    const newNoteUids = toContactNoteUids(newNotes);

    return newNoteUids.length ? newNoteUids : undefined;
  }, [newNotes]);

  return {
    linksContactNotes,
    notes: newNotes,
    changeNotes,
    existingNoteIds,
    blocker,
    uidsForSave,
  };
};
